import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import usePricingUsage, { loadUsage } from './usageStore';
import UpgradeModal from './UpgradeModal';
import AddCardPanel, { onAddCardRequest, openAddCard } from './AddCardPanel';
import { onUpgradeRequest, openUpgrade, PLAN_USAGE_PATH } from './PricingBits';
import { nextAlert } from './usageRules';
import { isOwnerHere, useAccess } from '../permissions/access';
import { isPublicPath, useStripeAlert } from '../settings/stripeStatus';
import { AGENT_VISIBLE, CONCIERGE_VISIBLE, PRICING_V2_VISIBLE } from '../../config/featureFlags';
import { hapticTick } from '../../_utils/haptics';
import './planUsage.css';

/**
 * Router-level host for pricing: the Bailey Plus modal (opened from any CTA
 * via openUpgrade) and one light alert at a time (settlement failed, balance
 * below zero, receptionist at 80% / 100%). Alerts never block: each can be
 * dismissed, and stays dismissed for that period. The Stripe bar wins the
 * corner while it is up.
 */
const DISMISS_KEY = 'beu.pricingAlertsDismissed';

function readDismissed() {
    try { return JSON.parse(localStorage.getItem(DISMISS_KEY)) || []; } catch (e) { return []; }
}
function writeDismissed(list) {
    try { localStorage.setItem(DISMISS_KEY, JSON.stringify(list.slice(-30))); } catch (e) { /* per visit then */ }
}

export function receptionistSetupPath() {
    if (AGENT_VISIBLE) return '/assistant/front-desk';
    return CONCIERGE_VISIBLE ? '/settings/ai-assistant' : PLAN_USAGE_PATH;
}

function PricingHostInner() {
    useAccess();
    const history = useHistory();
    const location = useLocation();
    let signedIn = false;
    try { signedIn = !!localStorage.getItem('accessToken'); } catch (e) { signedIn = false; }
    const owner = isOwnerHere();
    const onApp = signedIn && !isPublicPath(location.pathname);
    // Only fetch for an owner on an app page; others still get the modal
    // (with "ask the owner") from whatever surface already loaded usage.
    const [modal, setModal] = useState(null);
    const { usage } = usePricingUsage({ enabled: onApp && (owner || !!modal) });
    const stripe = useStripeAlert();
    const [dismissed, setDismissed] = useState(readDismissed);
    // The Add a card panel; `from: 'upgrade'` reopens the upgrade once saved.
    const [addCard, setAddCard] = useState(null);
    useEffect(() => onAddCardRequest((d) => setAddCard({ from: d.from || '' })), []);

    // Opening the modal refetches usage first (fresh=1 skips the server's plan
    // cache), so the charge line can't be stale; a failed refetch opens it
    // on what we already have.
    useEffect(() => onUpgradeRequest((d) => {
        setModal({ mode: d.mode || 'upgrade', ready: false });
        loadUsage(true, { fresh: true }).then(
            () => setModal((m) => (m ? { ...m, ready: true } : m)),
            () => setModal((m) => (m ? { ...m, ready: true } : m)),
        );
    }), []);

    const alert = onApp && owner && !stripe.active && location.pathname !== PLAN_USAGE_PATH ? nextAlert(usage, dismissed) : null;
    const dismiss = (a) => {
        const next = [...dismissed.filter((k) => k !== a.key), a.key];
        setDismissed(next);
        writeDismissed(next);
    };
    const act = (a) => {
        hapticTick();
        history.push(a.to || PLAN_USAGE_PATH);
    };

    return (
        <>
            {alert && (
                <div className={`pu-alert is-${alert.tone}`} role="status" aria-live="polite">
                    <div className="pu-alert-text">
                        <b>{alert.title}</b>
                        <span>{alert.text}</span>
                    </div>
                    <div className="pu-alert-actions">
                        <button type="button" className="pu-alert-cta" onClick={() => act(alert)}>{alert.cta}</button>
                        <button type="button" className="pu-alert-dismiss" onClick={() => dismiss(alert)} aria-label={`Dismiss: ${alert.title}`}>Dismiss</button>
                    </div>
                </div>
            )}
            {modal && modal.ready && usage && (
                <UpgradeModal
                    usage={usage}
                    isOwner={owner}
                    mode={modal.mode}
                    onClose={() => setModal(null)}
                    onGoTeam={() => history.push('/staff/permissions')}
                    onGoBilling={() => history.push(PLAN_USAGE_PATH, { open: 'payment' })}
                    onAddCard={() => { setModal(null); openAddCard({ from: 'upgrade' }); }}
                />
            )}
            {addCard && (
                <AddCardPanel
                    onClose={() => setAddCard(null)}
                    onSaved={() => { if (addCard.from === 'upgrade') setTimeout(() => openUpgrade({ from: 'card-saved' }), 350); }}
                />
            )}
        </>
    );
}

export default function PricingHost() {
    if (!PRICING_V2_VISIBLE) return null;
    return <PricingHostInner />;
}
